import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { IconBooks, IconSchool, IconBook2, IconBrandYoutube, IconNews } from '@tabler/icons-react';
import axios from 'axios';
import { API_URL } from '../../utils/api';

gsap.registerPlugin(ScrollTrigger);

function useCountUp(target, duration = 1.8, start = false) {
  const [count, setCount] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!start || target === 0) return;
    const startTime = performance.now();
    const animate = (now) => {
      const elapsed = (now - startTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration, start]);

  return count;
}

function StatCard({ stat, animate }) {
  const count = useCountUp(stat.rawValue, 1.8, animate);

  return (
    <div className="group p-6 sm:p-8 bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1 relative overflow-hidden">
      {/* Subtle background glow */}
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${stat.glow}`} />

      <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110 relative z-10 ${stat.color}`}>
        {stat.icon}
      </div>

      <h3 className="font-AndoraModernSerif text-3xl sm:text-4xl font-bold text-gray-900 mb-1 relative z-10">
        {animate ? count : 0}
        {stat.suffix}
      </h3>
      <p className="text-gray-500 font-medium text-sm uppercase tracking-wide relative z-10">
        {stat.label}
      </p>

      {/* Loading shimmer when not yet animated */}
      {!animate && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-pulse rounded-2xl" />
      )}
    </div>
  );
}

export function StatsSection() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef([]);
  const [animate, setAnimate] = useState(false);

  const [stats, setStats] = useState([
    {
      id: 'videos',
      label: 'Popular Videos',
      rawValue: 0,
      suffix: '+',
      icon: <IconBrandYoutube size={32} stroke={1.5} />,
      color: 'bg-blue-100 text-blue-600',
      glow: 'bg-blue-50/60',
    },
    {
      id: 'books',
      label: 'Own Books',
      rawValue: 0,
      suffix: '+',
      icon: <IconBooks size={32} stroke={1.5} />,
      color: 'bg-green-100 text-green-600',
      glow: 'bg-green-50/60',
    },
    {
      id: 'articles',
      label: 'Articles Published',
      rawValue: 0,
      suffix: '+',
      icon: <IconNews size={32} stroke={1.5} />,
      color: 'bg-pumpkinToast-100 text-pumpkinToast-600',
      glow: 'bg-pumpkinToast-50/60',
    },
    {
      id: 'courses',
      label: 'Popular Courses',
      rawValue: 0,
      suffix: '+',
      icon: <IconSchool size={32} stroke={1.5} />,
      color: 'bg-purple-100 text-purple-600',
      glow: 'bg-purple-50/60',
    },
  ]);

  // Fetch real data from API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch all in parallel
        const [videoRes, bookRes, articlesRes, courseRes] = await Promise.allSettled([
          axios.get(`${API_URL}/library/items?type=video`),
          axios.get(`${API_URL}/library/categories?type=book`),
          axios.get(`${API_URL}/articles`),
          axios.get(`${API_URL}/library/categories?type=course`),
        ]);

        // Try fetching all items to count by category type
        let videoCount = 0;
        let bookCount = 0;

        // Video items count
        if (videoRes.status === 'fulfilled' && Array.isArray(videoRes.value?.data)) {
          videoCount = videoRes.value.data.length;
        }
        // Fallback: fetch video categories, then count items per category
        if (videoCount === 0) {
          try {
            const vcatRes = await axios.get(`${API_URL}/library/categories?type=video`);
            const videoCats = Array.isArray(vcatRes.data) ? vcatRes.data : [];
            // Count items across all video categories
            const itemPromises = videoCats.map((cat) =>
              axios.get(`${API_URL}/library/items?category=${cat._id}`).catch(() => ({ data: [] }))
            );
            const itemResults = await Promise.all(itemPromises);
            videoCount = itemResults.reduce(
              (total, r) => total + (Array.isArray(r.data) ? r.data.length : 0),
              0
            );
          } catch (_) {}
        }

        // Book categories count (Own Books)
        if (bookRes.status === 'fulfilled' && Array.isArray(bookRes.value?.data)) {
          bookCount = bookRes.value.data.length;
        }

        // Articles count
        let articleCount = 0;
        if (articlesRes.status === 'fulfilled' && Array.isArray(articlesRes.value?.data)) {
          articleCount = articlesRes.value.data.length;
        }

        // Courses count
        let courseCount = 3; // default from hardcoded courses
        if (courseRes.status === 'fulfilled' && Array.isArray(courseRes.value?.data) && courseRes.value.data.length > 0) {
          courseCount = courseRes.value.data.length;
        }

        setStats((prev) =>
          prev.map((s) => {
            if (s.id === 'videos') return { ...s, rawValue: videoCount };
            if (s.id === 'books') return { ...s, rawValue: bookCount };
            if (s.id === 'articles') return { ...s, rawValue: articleCount };
            if (s.id === 'courses') return { ...s, rawValue: courseCount };
            return s;
          })
        );
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Title Animation
      gsap.fromTo(
        titleRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Cards Animation
      cardsRef.current.forEach((card, index) => {
        gsap.fromTo(
          card,
          { y: 50, opacity: 0, scale: 0.95 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.6,
            delay: index * 0.12,
            ease: 'back.out(1.7)',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 75%',
              toggleActions: 'play none none reverse',
              onEnter: () => setAnimate(true),
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full py-16 sm:py-20 bg-white relative overflow-hidden"
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gray-50 rounded-full blur-3xl opacity-60 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-pumpkinToast-50/40 rounded-full blur-3xl opacity-60 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10 text-center">
        {/* Header */}
        <div ref={titleRef} className="mb-12 sm:mb-16 max-w-2xl mx-auto">
          <span className="inline-block py-1 px-3 rounded-full bg-pumpkinToast-50 text-pumpkinToast-600 text-xs font-bold tracking-wider uppercase mb-3 border border-pumpkinToast-100">
            Our Impact
          </span>
          <h2 className="font-AndoraModernSerif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2a1273] mb-4">
            Empowering Through Knowledge
          </h2>
          <p className="text-gray-600 text-base sm:text-lg">
            Dedicated to spreading authentic Islamic teachings and fostering academic excellence.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {stats.map((stat, index) => (
            <div
              key={stat.id}
              ref={(el) => (cardsRef.current[index] = el)}
            >
              <StatCard stat={stat} animate={animate} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
